import React, { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Download, FileAudio, Loader2, Check, Music } from "lucide-react";
import { toast } from "sonner";
import NeonButton from "@/components/NeonButton";
import { cn } from "@/lib/utils";

const exportFormats = [
  { 
    id: "wav", 
    name: "WAV", 
    description: "Lossless, highest quality",
    mimeType: "audio/wav",
    quality: "Lossless",
    size: "Large"
  },
  { 
    id: "mp3", 
    name: "MP3", 
    description: "Compressed, universal compatibility",
    mimeType: "audio/mpeg",
    quality: "320kbps",
    size: "Medium"
  },
  { 
    id: "webm", 
    name: "WebM", 
    description: "Web optimized, good quality",
    mimeType: "audio/webm",
    quality: "High",
    size: "Small"
  },
  { 
    id: "ogg", 
    name: "OGG", 
    description: "Open format, efficient",
    mimeType: "audio/ogg",
    quality: "High",
    size: "Small"
  }
];

const AudioExporter = ({ 
  audioContext, 
  masterGain,
  isPlaying,
  sessionName = "Subspace Session"
}) => {
  const [selectedFormat, setSelectedFormat] = useState("wav");
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportDuration, setExportDuration] = useState(60); // seconds
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startExport = useCallback(async () => {
    if (!audioContext || !masterGain) {
      toast.error("No audio to export. Start playing first.");
      return;
    }

    if (!isPlaying) {
      toast.error("Please start playing audio first");
      return;
    }

    setIsExporting(true);
    setExportProgress(0);
    chunksRef.current = [];

    try {
      // Create a MediaStreamDestination
      const dest = audioContext.createMediaStreamDestination();
      masterGain.connect(dest);

      // Determine MIME type
      const format = exportFormats.find(f => f.id === selectedFormat);
      let mimeType = format?.mimeType || 'audio/webm';
      
      // Check browser support
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm;codecs=opus';
        toast.info("Using WebM format for best compatibility");
      }

      const mediaRecorder = new MediaRecorder(dest.stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        
        // Create download link
        const a = document.createElement('a');
        a.href = url;
        a.download = `${sessionName.replace(/\s+/g, '_')}_${Date.now()}.${selectedFormat === 'mp3' ? 'webm' : selectedFormat}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        // Disconnect
        try {
          masterGain.disconnect(dest);
        } catch (e) {}
        
        setIsExporting(false);
        setExportProgress(100);
        toast.success("Export complete! File downloaded.");
      };

      // Start recording
      mediaRecorder.start(1000);

      // Progress updates
      const progressInterval = setInterval(() => {
        setExportProgress(prev => {
          const next = prev + (100 / exportDuration);
          if (next >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return next;
        });
      }, 1000);

      // Auto-stop after duration
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
          clearInterval(progressInterval);
        }
      }, exportDuration * 1000);

    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed. Please try again.");
      setIsExporting(false);
    }
  }, [audioContext, masterGain, isPlaying, selectedFormat, exportDuration, sessionName]);

  const cancelExport = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsExporting(false);
    setExportProgress(0);
    toast.info("Export cancelled");
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Download size={16} className="text-green-400" />
          <h4 className="text-sm font-medium text-gray-300">Export Audio</h4>
        </div>
      </div>

      {isExporting ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass-card p-4 text-center"
        >
          <Loader2 className="animate-spin mx-auto mb-3 text-violet-400" size={32} />
          <div className="text-white font-medium mb-2">Exporting...</div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
            <motion.div
              className="h-full bg-gradient-to-r from-green-500 to-cyan-500"
              style={{ width: `${exportProgress}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mb-3">
            {Math.round(exportProgress)}% complete ({Math.round(exportDuration * exportProgress / 100)}s / {exportDuration}s)
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={cancelExport}
            className="px-4 py-2 text-sm bg-red-500/20 text-red-400 rounded-lg"
          >
            Cancel
          </motion.button>
        </motion.div>
      ) : (
        <>
          {/* Format Selection */}
          <div className="grid grid-cols-2 gap-2">
            {exportFormats.map((format) => (
              <motion.button
                key={format.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedFormat(format.id)}
                data-testid={`format-${format.id}`}
                className={cn(
                  "p-3 rounded-xl border text-left transition-all",
                  selectedFormat === format.id
                    ? "border-green-500 bg-green-500/20"
                    : "border-white/10 bg-white/5 hover:border-white/20"
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <FileAudio size={14} className={selectedFormat === format.id ? "text-green-400" : "text-gray-500"} />
                  <span className="text-sm font-medium text-white">{format.name}</span>
                  {selectedFormat === format.id && <Check size={12} className="text-green-400 ml-auto" />}
                </div>
                <p className="text-xs text-gray-500">{format.description}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-600">
                  <span>{format.quality}</span>
                  <span>•</span>
                  <span>{format.size}</span>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Duration Selection */}
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Export Duration</span>
              <span>{exportDuration}s ({Math.round(exportDuration / 60 * 10) / 10} min)</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="10"
                max="3540"
                step="10"
                value={exportDuration}
                onChange={(e) => setExportDuration(parseInt(e.target.value))}
                data-testid="export-duration-slider"
                className="flex-1"
              />
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>10s</span>
              <span>59 min</span>
            </div>
          </div>

          {/* Export Button */}
          <NeonButton 
            onClick={startExport}
            disabled={!isPlaying}
            data-testid="export-audio-btn"
            variant="cyan"
            className="w-full"
          >
            <Music size={16} className="mr-2" />
            Export {selectedFormat.toUpperCase()} ({Math.round(exportDuration / 60 * 10) / 10} min)
          </NeonButton>

          {!isPlaying && (
            <p className="text-xs text-center text-yellow-500">
              Start playing audio to enable export
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default AudioExporter;
